import { useEffect, useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Reveal } from "../components/Reveal";
import LocationMap from "../components/LocationMap";
import { usePageMeta } from "../lib/seo";

export default function Contact() {
  usePageMeta({
    title: "Contact",
    description:
      "Get in touch with Growing IT Solutions. Book a free AI consultation. Office in Ahmedabad, India — serving clients globally. contact@growingitsolutions.com.",
    path: "/contact",
  });
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", service: "", message: "" });
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", form);
      setDone(true);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", company: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="relative grid-bg border-b border-white/10">
        <div className="absolute inset-0 radial-glow pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-36 pb-16 sm:pt-44">
          <Reveal>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-primary mb-4">Contact</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold max-w-3xl leading-[1.07]">
              Let's talk about your AI project.
            </h1>
            <p className="mt-6 text-slate-400 max-w-2xl text-lg leading-relaxed">
              Tell us what you're building. We'll get back to you within 24 hours with next steps.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-24 grid lg:grid-cols-12 gap-12">
        {/* Info */}
        <Reveal className="lg:col-span-5">
          <div className="space-y-6">
            <a href="mailto:contact@growingitsolutions.com" data-testid="contact-email" className="flex items-start gap-4 border border-white/10 bg-[#0F111A] p-6 hover:border-primary/40 transition-colors">
              <span className="w-11 h-11 grid place-items-center bg-primary/10 border border-primary/30 text-primary shrink-0"><Mail className="w-5 h-5" /></span>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</p>
                <p className="text-white mt-1">contact@growingitsolutions.com</p>
              </div>
            </a>
            <a href="tel:+918799097310" data-testid="contact-phone" className="flex items-start gap-4 border border-white/10 bg-[#0F111A] p-6 hover:border-primary/40 transition-colors">
              <span className="w-11 h-11 grid place-items-center bg-primary/10 border border-primary/30 text-primary shrink-0"><Phone className="w-5 h-5" /></span>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Phone</p>
                <p className="text-white mt-1">+91 87990 97310</p>
              </div>
            </a>
            <div className="flex items-start gap-4 border border-white/10 bg-[#0F111A] p-6">
              <span className="w-11 h-11 grid place-items-center bg-primary/10 border border-primary/30 text-primary shrink-0"><MapPin className="w-5 h-5" /></span>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Office</p>
                <p className="text-slate-300 mt-1 leading-relaxed">B521, Money Plant High Street, Jagatpur Road, Gota, 382470, Ahmedabad, Gujarat, India</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm text-slate-400 px-2">
              <Clock className="w-4 h-4 text-primary" /> Typical response time: within 24 hours
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={120} className="lg:col-span-7">
          {done ? (
            <div data-testid="contact-success" className="border border-primary/30 bg-[#0F111A] p-12 text-center glow-blue">
              <CheckCircle2 className="w-14 h-14 text-primary mx-auto" />
              <h3 className="font-heading text-2xl font-semibold mt-5">Thank you!</h3>
              <p className="text-slate-400 mt-3">Your message has been received. We'll be in touch within 24 hours.</p>
              <button onClick={() => setDone(false)} className="mt-7 text-primary font-semibold text-sm">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="border border-white/10 bg-[#0F111A] p-7 sm:p-9 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name *"><input data-testid="contact-input-name" required value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" /></Field>
                <Field label="Email *"><input data-testid="contact-input-email" required type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@company.com" /></Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Company"><input data-testid="contact-input-company" value={form.company} onChange={update("company")} className={inputCls} placeholder="Company name" /></Field>
                <Field label="Phone"><input data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="+91 ..." /></Field>
              </div>
              <Field label="Service of interest">
                <select data-testid="contact-input-service" value={form.service} onChange={update("service")} className={inputCls}>
                  <option value="">Select a service</option>
                  {services.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                  <option value="Other">Other / Not sure</option>
                </select>
              </Field>
              <Field label="How can we help? *">
                <textarea data-testid="contact-input-message" required rows={5} value={form.message} onChange={update("message")} className={inputCls} placeholder="Tell us about your project..." />
              </Field>
              <button data-testid="contact-submit" type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-4 glow-blue transition-all disabled:opacity-60">
                {submitting ? "Sending..." : <>Send Message <Send className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </Reveal>
      </section>

      {/* MAP */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        <Reveal>
          <div className="flex items-center gap-3 mb-6">
            <MapPin className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-2xl font-semibold">Visit our office</h2>
          </div>
          <LocationMap height="h-[440px]" />
        </Reveal>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-[#05060A] border border-white/10 focus:border-primary outline-none text-white px-4 py-3 transition-colors placeholder:text-slate-600";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</span>
    {children}
  </label>
);
